"use client"

import { useState } from "react"
import { FaPlay } from "react-icons/fa"
import Image from "next/image"
import { FaDiscord, FaTiktok, FaInstagram, FaYoutube } from "react-icons/fa6"
import { useTranslations } from "next-intl"

const VIDEO_ID = "9DiSx7BpUFg"
const THUMBNAIL = `https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`

const YouTubeCard = ({ t }: { t: any }) => {
  const [playing, setPlaying] = useState(false)

  return (
    <div className="relative w-full max-w-md">
      <div
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] opacity-70 blur-3xl"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 50%, rgba(124,68,229,0.35), transparent 70%)",
        }}
      />

      <div
        onClick={() => !playing && setPlaying(true)}
        className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] shadow-[0_20px_60px_-20px_rgba(124,68,229,0.45),inset_0_1px_0_0_rgba(255,255,255,0.05)] backdrop-blur-sm transition-all duration-300 hover:border-purple-400/30 hover:shadow-[0_25px_70px_-15px_rgba(124,68,229,0.55),inset_0_1px_0_0_rgba(255,255,255,0.08)]"
        style={{ aspectRatio: "16/9" }}
      >
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
            allow="autoplay; encrypted-media"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <div className="relative h-full w-full">
            <Image
              src={THUMBNAIL}
              alt="Aurex Hosting video"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30 transition-opacity duration-300 group-hover:opacity-90" />

            <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-2xl pointer-events-none" />

            <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 backdrop-blur-md">
              <svg viewBox="0 0 24 24" className="h-3 w-3 flex-shrink-0" fill="#ffffff">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span className="font-poppins text-[10px] font-medium tracking-wide text-white/90">YouTube</span>
            </div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex items-center justify-center">
                <span
                  aria-hidden="true"
                  className="absolute h-20 w-20 rounded-full bg-purple-500/30 blur-md transition-opacity duration-300 group-hover:opacity-100 opacity-70"
                />
                <span
                  aria-hidden="true"
                  className="absolute h-16 w-16 rounded-full border border-white/30"
                />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-white/95 shadow-[0_12px_30px_-10px_rgba(124,68,229,0.6)] backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                  <FaPlay className="ml-0.5 h-4 w-4 text-[#0a0a14]" />
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-3 px-4 pb-4">
              <div className="flex flex-col">
                <span className="font-akira text-sm tracking-wide text-white">{t('watch_story')}</span>
                <span className="font-poppins text-[11px] text-white/55">{t('youtube')}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const About = () => {
  const t = useTranslations('About')
  return (
    <section className=" relative z-10 w-full overflow-x-clip">
          <div
            className="pointer-events-none absolute top-2/3 left-2/5 h-[400px] w-[200vw] lg:w-[1100px] rounded-full opacity-25 blur-3xl"
            style={{ backgroundImage: "radial-gradient(at 0% 0%, #7c44e5a2, transparent)" }}
          />
     
      <div className="mb-8  max-w-7xl mx-auto px-4 pb-8 flex flex-col lg:flex-row items-center gap-16 relative">
       
        <div className="flex-1 flex flex-col gap-6">
           <div className="relative bg-[#6D28D9] rounded-tr-xl rounded-bl-xl  inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white font-poppins select-none w-fit">
          {t('badge')}
        </div>
        
          <p className="text-gray-400 font-poppins text-base leading-relaxed">
            {t('desc1')}<span className="text-white font-medium">{t('desc2')}</span>{t('desc3')}
          </p>
          <div className="flex items-center gap-5">
            <a href="https://discord.com/invite/aurex" target="_blank" rel="noopener noreferrer" aria-label="Discord" className="text-gray-500 hover:text-[#5865F2] transition-colors duration-200">
              <FaDiscord size={22} />
            </a>
            <a href="https://www.tiktok.com/@aurex.lk" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-gray-500 hover:text-white transition-colors duration-200">
              <FaTiktok size={22} />
            </a>
            <a href="https://www.instagram.com/aurex.lk" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-500 hover:text-pink-500 transition-colors duration-200">
              <FaInstagram size={22} />
            </a>
            <a href="https://www.youtube.com/@aurexhosting" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-gray-500 hover:text-red-500 transition-colors duration-200">
              <FaYoutube size={22} />
            </a>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-end">
          <YouTubeCard t={t} />
        </div>
      </div>
    </section>
  )
}

export default About
